import { useState, useEffect } from 'react';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Loader, Tabs } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { Breadcrumbs } from '@components';
import { Button, ControlledInput, RefetchButton, Switch, Tooltip } from '@shared/ui';
import {
  useBonusRules,
  useCreateBonusRule,
  useUpdateBonusRule,
  useDeleteBonusRule,
  useRunActivatePendingCron,
  useRunExpireCron,
  useRunSendExpiringRemindersCron,
  ManualBonusForm,
  type BonusRule,
} from '@modules/bonuses';
import { IoPlay, IoFlame, IoNotifications, IoInformationCircleOutline } from 'react-icons/io5';
import { bonusRuleFormSchema } from '@modules/bonuses/utils/bonusValidation';
import '@shared/styles/page.css';
import './BonusesPage.css';

const BONUSES_PAGE_HINT =
  'Настройте процент кэшбэка за заказы и при необходимости начисляйте бонусы покупателям вручную. Бонусы вашего магазина покупатель может тратить только у вас.';

interface BonusRuleFormData {
  bonusPercent: number;
  minOrderAmount?: number | null;
  maxBonusAmount?: number | null;
  expiresDays?: number | null;
  isActive: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
}

const BONUS_DATE_TZ = 'Europe/Moscow';

function toBonusDateIso(v: Date | string | null | undefined): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') {
    const parsed = new Date(v);
    if (Number.isNaN(parsed.getTime())) return v;
    return toBonusDateIso(parsed);
  }
  if (!(v instanceof Date) || Number.isNaN(v.getTime())) return undefined;
  return new Date(
    Date.UTC(v.getFullYear(), v.getMonth(), v.getDate(), 12, 0, 0),
  ).toISOString();
}

function fromBonusDateIso(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return d;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BONUS_DATE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return new Date(Number(map.year), Number(map.month) - 1, Number(map.day));
}

export default function BonusesPage() {
  const [editingRule, setEditingRule] = useState<BonusRule | null>(null);

  const {
    data: rules = [],
    isLoading: rulesLoading,
    error,
    refetch,
    isFetching: rulesFetching,
  } = useBonusRules();
  const createRule = useCreateBonusRule();
  const updateRule = useUpdateBonusRule();
  const deleteRule = useDeleteBonusRule();
  const runActivateCron = useRunActivatePendingCron();
  const runExpireCron = useRunExpireCron();
  const runExpiringRemindersCron = useRunSendExpiringRemindersCron();

  const isLoading =
    rulesLoading ||
    createRule.isPending ||
    updateRule.isPending ||
    deleteRule.isPending ||
    runActivateCron.isPending ||
    runExpireCron.isPending ||
    runExpiringRemindersCron.isPending;

  const resolver = yupResolver(bonusRuleFormSchema) as any;

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<BonusRuleFormData>({
    resolver,
    defaultValues: {
      bonusPercent: 0,
      minOrderAmount: null,
      maxBonusAmount: null,
      expiresDays: null,
      isActive: true,
      startDate: null,
      endDate: null,
    },
  });

  const { field: isActiveField } = useController({ name: 'isActive', control });
  const { field: startDateField } = useController({ name: 'startDate', control });
  const { field: endDateField } = useController({ name: 'endDate', control });

  const editingRuleId = editingRule?.id;
  const activeRule = editingRule ?? rules[0] ?? null;
  const activeRuleSyncKey = activeRule
    ? [
      activeRule.id,
      activeRule.isActive,
      activeRule.bonusPercent,
      activeRule.minOrderAmount,
      activeRule.maxBonusAmount,
      activeRule.expiresDays,
      activeRule.startDate,
      activeRule.endDate,
    ].join(':')
    : 'empty';

  const ruleToFormValues = (rule: BonusRule): BonusRuleFormData => ({
    bonusPercent: rule.bonusPercent,
    minOrderAmount: rule.minOrderAmount ?? null,
    maxBonusAmount: rule.maxBonusAmount ?? null,
    expiresDays: rule.expiresDays ?? null,
    isActive: rule.isActive,
    startDate: rule.startDate ? fromBonusDateIso(rule.startDate) : null,
    endDate: rule.endDate ? fromBonusDateIso(rule.endDate) : null,
  });

  useEffect(() => {
    if (editingRule) {
      reset(ruleToFormValues(editingRule));
      return;
    }
    if (rules.length > 0) {
      reset(ruleToFormValues(rules[0]));
      return;
    }
    reset({
      bonusPercent: 0,
      minOrderAmount: null,
      maxBonusAmount: null,
      expiresDays: null,
      isActive: true,
      startDate: null,
      endDate: null,
    });
  }, [editingRuleId, activeRuleSyncKey, reset]);

  const onSubmit = async (data: BonusRuleFormData) => {
    const submitData: Record<string, unknown> = {
      bonusPercent: data.bonusPercent,
      isActive: data.isActive,
    };

    submitData.minOrderAmount = data.minOrderAmount ?? null;
    submitData.maxBonusAmount = data.maxBonusAmount ?? null;
    submitData.expiresDays = data.expiresDays ?? null;
    submitData.startDate = data.startDate ? toBonusDateIso(data.startDate) : null;
    submitData.endDate = data.endDate ? toBonusDateIso(data.endDate) : null;

    if (editingRule) {
      await updateRule.mutateAsync({ id: editingRule.id, data: submitData as any });
    } else {
      await createRule.mutateAsync(submitData as any);
    }

    // Не вызываем reset() — значение остаётся, а после refetch эффект синхронизирует с сервером
    setEditingRule(null);
  };

  const handleEditFirstRule = () => {
    if (rules.length > 0) {
      setEditingRule(rules[0]);
    }
  };

  const handleCancel = () => {
    setEditingRule(null);
  };

  const hasRules = rules.length > 0;
  const isFormDisabled = hasRules && !editingRule;

  const handleActiveToggle = async (checked: boolean) => {
    isActiveField.onChange(checked);
    // В режиме просмотра сразу сохраняем только флаг активности
    if (!isFormDisabled || !rules[0]) {
      return;
    }
    try {
      await updateRule.mutateAsync({
        id: rules[0].id,
        data: { isActive: checked },
      });
    } catch {
      isActiveField.onChange(!checked);
    }
  };

  const rulesStatus = (() => {
    if (rulesLoading) {
      return (
        <div className="empty-container">
          <Loader />
        </div>
      );
    }
    if (error && refetch) {
      return (
        <RefetchButton refetch={refetch} isLoading={rulesLoading || rulesFetching} />
      );
    }
    return null;
  })();

  return (
    <div className="page-container bonuses-page">
      <Breadcrumbs
        items={[{ label: 'Главная', path: '/' }, { label: 'Бонусная система' }]}
      />

      <Tabs defaultValue="rules" keepMounted={false} className="bonuses-tabs">
        <div className="page-header">
          <div className="page-header-top bonuses-page-header-top">
            <div className="bonuses-page-title-row">
              <h1>Бонусная система</h1>
              <Tooltip
                label={BONUSES_PAGE_HINT}
                withArrow
                position="bottom"
                multiline
                w={280}
                events={{ hover: true, focus: true, touch: true }}
              >
                <button
                  type="button"
                  className="bonuses-page-info-btn"
                  aria-label="О бонусной системе"
                >
                  <IoInformationCircleOutline size={20} aria-hidden />
                </button>
              </Tooltip>
            </div>
            <Tabs.List className="bonuses-tabs-list">
              <Tabs.Tab value="rules" className="bonuses-tabs-tab">
                Правило
              </Tabs.Tab>
              <Tabs.Tab value="manual" className="bonuses-tabs-tab">
                Ручное начисление
              </Tabs.Tab>
            </Tabs.List>
          </div>
          <p className="bonuses-page-subtitle">{BONUSES_PAGE_HINT}</p>
        </div>

        <Tabs.Panel value="rules" className="bonuses-tabs-panel">
          <div className="content-wrapper bonuses-content">
            {rulesStatus ?? (
              <form onSubmit={handleSubmit(onSubmit)} className="bonuses-form">
                <div className="bonuses-form-title-row">
                  <h2 className="bonuses-form-title">
                    {editingRule
                      ? 'Редактировать правило'
                      : hasRules
                        ? 'Правило начисления'
                        : 'Создать правило'}
                  </h2>
                  <Switch
                    className="bonuses-form-active-switch"
                    label="Активно"
                    checked={isActiveField.value}
                    onChange={(event) =>
                      void handleActiveToggle(event.currentTarget.checked)
                    }
                    onBlur={isActiveField.onBlur}
                    disabled={updateRule.isPending}
                  />
                </div>

                <div className="bonuses-form-grid">
                  <div className="bonuses-form-field">
                    <ControlledInput
                      id="bonusPercent"
                      type="number"
                      label="Процент бонусов (%) *"
                      name="bonusPercent"
                      control={control}
                      error={errors.bonusPercent}
                      placeholder="5"
                      min={0}
                      max={100}
                      step="0.01"
                      description="Процент от суммы заказа, который начисляется бонусами"
                      disabled={isFormDisabled}
                    />
                  </div>

                  <div className="bonuses-form-field">
                    <ControlledInput
                      id="minOrderAmount"
                      type="number"
                      label="Минимальная сумма заказа"
                      name="minOrderAmount"
                      control={control}
                      error={errors.minOrderAmount}
                      placeholder="0"
                      min={0}
                      step="0.01"
                      description="Пусто — бонусы начисляются на любую сумму заказа"
                      disabled={isFormDisabled}
                    />
                  </div>

                  <div className="bonuses-form-field">
                    <ControlledInput
                      id="maxBonusAmount"
                      type="number"
                      label="Максимальная сумма бонусов"
                      name="maxBonusAmount"
                      control={control}
                      error={errors.maxBonusAmount}
                      placeholder="0"
                      min={0}
                      step="0.01"
                      description="Пусто — без лимита на начисление за заказ"
                      disabled={isFormDisabled}
                    />
                  </div>

                  <div className="bonuses-form-field">
                    <ControlledInput
                      id="expiresDays"
                      type="number"
                      label="Срок действия (дней)"
                      name="expiresDays"
                      control={control}
                      error={errors.expiresDays}
                      placeholder="Не ограничен"
                      min={1}
                      description="Пусто — бонусы не сгорают"
                      disabled={isFormDisabled}
                    />
                  </div>

                  <div className="bonuses-form-field">
                    <DateInput
                      className="bonuses-date-input custom-date-input"
                      label="Дата начала"
                      placeholder="Не указано"
                      valueFormat="DD.MM.YYYY"
                      value={startDateField.value}
                      onChange={(value) => startDateField.onChange(value)}
                      onBlur={startDateField.onBlur}
                      clearable
                      error={errors.startDate?.message}
                      description="Пусто — правило действует с момента создания"
                      disabled={isFormDisabled}
                    />
                  </div>

                  <div className="bonuses-form-field">
                    <DateInput
                      className="bonuses-date-input custom-date-input"
                      label="Дата окончания"
                      placeholder="Не указано"
                      valueFormat="DD.MM.YYYY"
                      value={endDateField.value}
                      onChange={(value) => endDateField.onChange(value)}
                      onBlur={endDateField.onBlur}
                      clearable
                      error={errors.endDate?.message}
                      description="Пусто — правило действует бессрочно"
                      disabled={isFormDisabled}
                    />
                  </div>
                </div>

                <div className="bonuses-form-actions">
                  {isFormDisabled ? (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleEditFirstRule}
                      disabled={isLoading}
                      loading={isLoading}
                    >
                      Редактировать
                    </Button>
                  ) : (
                    <>
                      {editingRule ? (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleCancel}
                          disabled={isLoading}
                        >
                          Отмена
                        </Button>
                      ) : null}
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        loading={isLoading}
                      >
                        {editingRule ? 'Сохранить' : 'Создать правило'}
                      </Button>
                    </>
                  )}
                </div>

                {!import.meta.env.PROD ? (
                  <div className="bonuses-cron-row">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => runActivateCron.mutate()}
                      disabled={runActivateCron.isPending}
                      leftAddon={<IoPlay size={16} />}
                    >
                      {runActivateCron.isPending ? 'Запуск…' : 'Крон: PENDING → ACTIVE'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => runExpireCron.mutate()}
                      disabled={runExpireCron.isPending}
                      leftAddon={<IoFlame size={16} />}
                    >
                      {runExpireCron.isPending ? 'Запуск…' : 'Крон: сгорание'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => runExpiringRemindersCron.mutate()}
                      disabled={runExpiringRemindersCron.isPending}
                      leftAddon={<IoNotifications size={16} />}
                    >
                      {runExpiringRemindersCron.isPending
                        ? 'Запуск…'
                        : 'Крон: пуши «скоро сгорят»'}
                    </Button>
                  </div>
                ) : null}
              </form>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="manual" className="bonuses-tabs-panel">
          <div className="content-wrapper bonuses-content">
            <ManualBonusForm />
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
